import { FLOAT_NUMBER_WITH_MAX_TWO_DECIMALS } from '@openx/components-form/validationRegexes.ts';
import type { TFunction } from 'i18next';
import { array, boolean, mixed, object, string } from 'yup';
import type { RevenueMethod } from '../AudienceForm/constants.ts';

/** 0–99 integer (no empty, no 3+ digits) */
const PERCENT_0_TO_99 = /^(?:[0-9]|[1-9][0-9])$/;
const MAX_CPM_VALUE = 1000000;

export const getAudienceShareItemSchema = (t: TFunction) =>
  object({
    recipient_account_id: array(string().required()).defined(),
    audience_external_openaudience_id: string().required(),
    isFeeEnabled: boolean().defined(),
    revenue_method: mixed<RevenueMethod>().oneOf(['CPM', 'PoM']).defined(),
    value: string()
      .defined()
      .when('isFeeEnabled', {
        is: true,
        then: str =>
          str.required(t('Value is required')).when('revenue_method', {
            is: (revenueMethod: RevenueMethod) => revenueMethod === 'PoM',
            then: schema => schema.matches(PERCENT_0_TO_99, t('Enter a whole number between 0 and 99')),
            otherwise: schema =>
              schema
                .test(
                  'Maximum value',
                  t('Entered value cannot be bigger than {maxValue}.', { maxValue: MAX_CPM_VALUE }),
                  (value = '') => {
                    const wholeNumber = value.split('.')[0];
                    const maxNumberString = MAX_CPM_VALUE.toString();

                    return wholeNumber.length < maxNumberString.length || wholeNumber === maxNumberString;
                  }
                )
                .matches(FLOAT_NUMBER_WITH_MAX_TWO_DECIMALS, t('Enter a positive number with up to two decimals')),
          }),
        otherwise: schema => schema,
      }),
  });

export const getAudienceShareFormSchema = (t: TFunction) => {
  return object({
    audience_share: array(getAudienceShareItemSchema(t)).required(),
  });
};
